import { createSlice, type PayloadAction } from "@reduxjs/toolkit"

import type { Room } from "@/api/types"
import { logoutAction } from "@/store/session/logoutAction"

interface RoomCacheEntry {
  room: Room
  lastUpdated: number
}

interface RoomsState {
  rooms: Record<string, RoomCacheEntry>
}

const initialState: RoomsState = {
  rooms: {},
}

const roomsSlice = createSlice({
  name: "rooms",
  initialState,
  reducers: {
    setRoom: {
      reducer(
        state,
        action: PayloadAction<{ room: Room; lastUpdated: number }>,
      ) {
        state.rooms[action.payload.room.id] = action.payload
      },
      prepare(room: Room) {
        return { payload: { room, lastUpdated: Date.now() } }
      },
    },
    updateRoomSettings(
      state,
      action: PayloadAction<{
        roomId: string
        roomSettings: Partial<Room["roomSettings"]>
      }>,
    ) {
      const entry = state.rooms[action.payload.roomId]
      if (entry) {
        entry.room = {
          ...entry.room,
          roomSettings: {
            ...entry.room.roomSettings,
            ...action.payload.roomSettings,
          },
        }
        entry.lastUpdated = Date.now()
      }
    },
    removeRoom(state, action: PayloadAction<string>) {
      delete state.rooms[action.payload]
    },
    clearRooms(state) {
      state.rooms = {}
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logoutAction, () => initialState)
  },
})

export const { setRoom, removeRoom, clearRooms, updateRoomSettings } =
  roomsSlice.actions
export default roomsSlice.reducer
